/**
 * BelajarKu Learning Engine V2 — LE-V2-04 Batch Submission & Evaluation Contract.
 *
 * IMPORTANT:
 * - This contract extends Mathematics v1. It does not replace the v1 grader.
 * - First-pass batch evaluation is deferred when any item needs correction.
 * - No authoritative attempt/evidence/mastery write should happen until a batch
 *   is either fully correct or finalized by LE-V2-05 Correction Mode.
 */

export const LE_V2_04_CONTRACT_VERSION = 'le-v2-04.1' as const;
export const LE_V2_04_SCHEMA_VERSION = 2 as const;

export type ExactIntegerResponse = {
  type: 'integer';
  value: number;
};

// Intentionally extensible for future subjects/renderers without changing the
// batch envelope. Mathematics v1 currently uses ExactIntegerResponse.
export type StudyResponse = ExactIntegerResponse | Record<string, unknown>;

export type BatchInkReference = {
  storageKey?: string;
  checksum?: string;
  strokeCount?: number;
  pointCount?: number;
  inputType?: 'pen' | 'touch' | 'mouse' | 'mixed' | string;
  durationMs?: number;
  formatVersion?: string;
};

export type BatchSubmissionItemV2 = {
  sequence: number;
  assignmentItemId: string;
  questionInstanceId: string;
  response: StudyResponse;
  startedAt: string;
  completedAt: string;
  elapsedDurationMs: number;
  activeDurationMs: number;
  hintCount: number;
  ink?: BatchInkReference | null;
};

export type BatchSubmissionV2 = {
  schemaVersion: 2;
  contractVersion: typeof LE_V2_04_CONTRACT_VERSION;
  clientBatchSubmissionId: string;
  studySessionId: string;
  assignmentId: string;
  worksheetVersionId: string;
  submittedAt: string;
  telemetry: {
    elapsedDurationMs: number;
    activeDurationMs: number;
    pausedDurationMs: number;
    eventCursor?: string | number | null;
  };
  items: BatchSubmissionItemV2[];
};

export type BatchItemEvaluationStatus = 'correct' | 'incorrect' | 'review_required';

export type BatchItemEvaluationV2 = {
  assignmentItemId: string;
  questionInstanceId: string;
  status: BatchItemEvaluationStatus;
  firstAttemptCorrect: boolean | null;
  requiresCorrection: boolean;
  graderVersion: string | null;
  feedbackCode?: string | null;
};

export type BatchEvaluationSummaryV2 = {
  itemCount: number;
  correctCount: number;
  incorrectCount: number;
  reviewRequiredCount: number;
  firstAttemptAccuracy: number | null;
  items: BatchItemEvaluationV2[];
};

export type BatchSubmissionStatusV2 =
  | 'completed'
  | 'correction_required'
  | 'review_required'
  | 'duplicate'
  | 'rejected';

export type BatchSubmissionResultV2 = {
  schemaVersion: 2;
  contractVersion: typeof LE_V2_04_CONTRACT_VERSION;
  clientBatchSubmissionId: string;
  status: BatchSubmissionStatusV2;
  originalStatus?: Exclude<BatchSubmissionStatusV2, 'duplicate'>;
  evaluation: BatchEvaluationSummaryV2;
  correction: {
    required: boolean;
    questionInstanceIds: string[];
    mode: 'student_correction' | 'none';
  };
  commit: {
    state: 'finalized' | 'deferred';
    finalizedItemCount: number;
  };
  nextMode: 'study_complete' | 'correction' | 'await_review';
};

export type ValidationIssue = {
  code: string;
  path: string;
  message: string;
};

const UUIDISH = /^[0-9a-f]{8}-[0-9a-f-]{27,}$/i;

function isIsoDate(value: unknown): value is string {
  return typeof value === 'string' && value.length >= 20 && Number.isFinite(Date.parse(value));
}

function nonNegativeInteger(value: unknown): value is number {
  return Number.isInteger(value) && Number(value) >= 0;
}

function validateUuidish(value: unknown, path: string, issues: ValidationIssue[]) {
  if (typeof value !== 'string' || !UUIDISH.test(value)) {
    issues.push({ code: 'INVALID_ID', path, message: `${path} must be a UUID-like identifier.` });
  }
}

/**
 * Structural/client validation only. Authoritative manifest ownership,
 * worksheet version, frozen question and exact required-item checks belong to
 * the server RPC.
 */
export function validateBatchSubmissionV2(input: unknown): ValidationIssue[] {
  const issues: ValidationIssue[] = [];
  if (!input || typeof input !== 'object') {
    return [{ code: 'INVALID_BATCH', path: '$', message: 'Batch submission must be an object.' }];
  }

  const batch = input as Record<string, unknown>;
  if (batch.schemaVersion !== LE_V2_04_SCHEMA_VERSION) {
    issues.push({ code: 'INVALID_SCHEMA_VERSION', path: 'schemaVersion', message: 'schemaVersion must be 2.' });
  }
  if (batch.contractVersion !== LE_V2_04_CONTRACT_VERSION) {
    issues.push({ code: 'INVALID_CONTRACT_VERSION', path: 'contractVersion', message: `contractVersion must be ${LE_V2_04_CONTRACT_VERSION}.` });
  }
  if (typeof batch.clientBatchSubmissionId !== 'string' || batch.clientBatchSubmissionId.trim().length < 8 || batch.clientBatchSubmissionId.trim().length > 128) {
    issues.push({ code: 'INVALID_BATCH_ID', path: 'clientBatchSubmissionId', message: 'clientBatchSubmissionId must contain 8–128 characters.' });
  }
  validateUuidish(batch.studySessionId, 'studySessionId', issues);
  validateUuidish(batch.assignmentId, 'assignmentId', issues);
  validateUuidish(batch.worksheetVersionId, 'worksheetVersionId', issues);
  if (!isIsoDate(batch.submittedAt)) {
    issues.push({ code: 'INVALID_SUBMITTED_AT', path: 'submittedAt', message: 'submittedAt must be an ISO timestamp.' });
  }

  const telemetry = batch.telemetry as Record<string, unknown> | null | undefined;
  if (!telemetry || typeof telemetry !== 'object') {
    issues.push({ code: 'INVALID_TELEMETRY', path: 'telemetry', message: 'telemetry is required.' });
  } else {
    for (const key of ['elapsedDurationMs', 'activeDurationMs', 'pausedDurationMs'] as const) {
      if (!nonNegativeInteger(telemetry[key])) {
        issues.push({ code: 'INVALID_TELEMETRY', path: `telemetry.${key}`, message: `${key} must be a non-negative integer.` });
      }
    }
    if (
      nonNegativeInteger(telemetry.elapsedDurationMs) &&
      nonNegativeInteger(telemetry.activeDurationMs) &&
      telemetry.activeDurationMs > telemetry.elapsedDurationMs
    ) {
      issues.push({ code: 'ACTIVE_EXCEEDS_ELAPSED', path: 'telemetry.activeDurationMs', message: 'Active duration cannot exceed elapsed duration.' });
    }
    if (
      nonNegativeInteger(telemetry.elapsedDurationMs) &&
      nonNegativeInteger(telemetry.pausedDurationMs) &&
      telemetry.pausedDurationMs > telemetry.elapsedDurationMs
    ) {
      issues.push({ code: 'PAUSED_EXCEEDS_ELAPSED', path: 'telemetry.pausedDurationMs', message: 'Paused duration cannot exceed elapsed duration.' });
    }
  }

  if (!Array.isArray(batch.items) || batch.items.length === 0) {
    issues.push({ code: 'EMPTY_BATCH', path: 'items', message: 'At least one batch item is required.' });
    return issues;
  }
  if (batch.items.length > 200) {
    issues.push({ code: 'BATCH_TOO_LARGE', path: 'items', message: 'Batch cannot contain more than 200 items.' });
  }

  const questionIds = new Set<string>();
  const sequences = new Set<number>();
  batch.items.forEach((raw, index) => {
    const path = `items[${index}]`;
    if (!raw || typeof raw !== 'object') {
      issues.push({ code: 'INVALID_ITEM', path, message: 'Batch item must be an object.' });
      return;
    }
    const item = raw as Record<string, unknown>;

    // LE-V2-04 first pass is intentionally NOT the correction payload.
    if ('firstResponse' in item || 'correctionCount' in item || 'correctedResponse' in item) {
      issues.push({ code: 'CORRECTION_FIELD_NOT_ALLOWED', path, message: 'Correction fields are reserved for LE-V2-05.' });
    }

    if (!nonNegativeInteger(item.sequence)) {
      issues.push({ code: 'INVALID_SEQUENCE', path: `${path}.sequence`, message: 'sequence must be a non-negative integer.' });
    } else if (sequences.has(item.sequence)) {
      issues.push({ code: 'DUPLICATE_SEQUENCE', path: `${path}.sequence`, message: 'sequence must be unique inside the batch.' });
    } else {
      sequences.add(item.sequence);
    }

    validateUuidish(item.assignmentItemId, `${path}.assignmentItemId`, issues);
    validateUuidish(item.questionInstanceId, `${path}.questionInstanceId`, issues);
    if (typeof item.questionInstanceId === 'string') {
      if (questionIds.has(item.questionInstanceId)) {
        issues.push({ code: 'DUPLICATE_QUESTION', path: `${path}.questionInstanceId`, message: 'questionInstanceId must be unique inside the batch.' });
      }
      questionIds.add(item.questionInstanceId);
    }

    if (!item.response || typeof item.response !== 'object') {
      issues.push({ code: 'MISSING_RESPONSE', path: `${path}.response`, message: 'response is required.' });
    }
    if (!isIsoDate(item.startedAt)) {
      issues.push({ code: 'INVALID_STARTED_AT', path: `${path}.startedAt`, message: 'startedAt must be an ISO timestamp.' });
    }
    if (!isIsoDate(item.completedAt)) {
      issues.push({ code: 'INVALID_COMPLETED_AT', path: `${path}.completedAt`, message: 'completedAt must be an ISO timestamp.' });
    }
    for (const key of ['elapsedDurationMs', 'activeDurationMs', 'hintCount'] as const) {
      if (!nonNegativeInteger(item[key])) {
        issues.push({ code: 'INVALID_ITEM_TELEMETRY', path: `${path}.${key}`, message: `${key} must be a non-negative integer.` });
      }
    }
    if (
      nonNegativeInteger(item.elapsedDurationMs) &&
      nonNegativeInteger(item.activeDurationMs) &&
      item.activeDurationMs > item.elapsedDurationMs
    ) {
      issues.push({ code: 'ITEM_ACTIVE_EXCEEDS_ELAPSED', path: `${path}.activeDurationMs`, message: 'Item active duration cannot exceed item elapsed duration.' });
    }
  });

  return issues;
}

/**
 * Stable child id used when a batch is finally committed through the existing
 * Mathematics v1 submission primitive. It must not change across retries.
 */
export function deriveBatchItemSubmissionId(clientBatchSubmissionId: string, questionInstanceId: string): string {
  return `le-v2-04:${clientBatchSubmissionId}:q:${questionInstanceId}`;
}

export function buildEvaluationSummary(items: BatchItemEvaluationV2[]): BatchEvaluationSummaryV2 {
  const correctCount = items.filter((item) => item.status === 'correct').length;
  const incorrectCount = items.filter((item) => item.status === 'incorrect').length;
  const reviewRequiredCount = items.filter((item) => item.status === 'review_required').length;
  const autoGraded = correctCount + incorrectCount;
  return {
    itemCount: items.length,
    correctCount,
    incorrectCount,
    reviewRequiredCount,
    firstAttemptAccuracy: autoGraded === 0 ? null : Math.round((correctCount / autoGraded) * 10000) / 100,
    items,
  };
}

export function decideBatchOutcome(summary: BatchEvaluationSummaryV2): {
  status: 'completed' | 'correction_required' | 'review_required';
  commitState: 'finalized' | 'deferred';
  nextMode: 'study_complete' | 'correction' | 'await_review';
} {
  if (summary.reviewRequiredCount > 0) {
    return { status: 'review_required', commitState: 'deferred', nextMode: 'await_review' };
  }
  if (summary.incorrectCount > 0) {
    return { status: 'correction_required', commitState: 'deferred', nextMode: 'correction' };
  }
  return { status: 'completed', commitState: 'finalized', nextMode: 'study_complete' };
}

export function correctionQuestionIds(summary: BatchEvaluationSummaryV2): string[] {
  return summary.items.filter((item) => item.status === 'incorrect').map((item) => item.questionInstanceId);
}
